import React from 'react';

import { Grid, Typography } from '@mui/material';

import { Job } from '@libs/graphql-types';
import { modelPath } from '@libs/helpers/form';
import { ConnectedDatePicker } from '@libs/ui/components/form/datepicker';
import { ConnectedRadioGroup } from '@libs/ui/components/form/radio/Radio';
import { ConnectedSelect } from '@libs/ui/components/form/select';
import { ConnectedTextField } from '@libs/ui/components/form/text-field';
import StepSection from '@libs/ui/components/step-section';

import { SocialSecurityId, LocationCodes, WorkerPayType } from '../components';
import { ApplicationFormState } from '../types';

interface DetailsSectionProps {
  showOnlyRequired: boolean;
  job: Job;
}

const YES_NO_OPTIONS = [
  { value: 'true', text: 'Yes' },
  { value: 'false', text: 'No' },
];

const EDUCATION_OPTIONS = [
  { text: 'High School', value: 'high_school' },
  { text: 'Associate Degree', value: 'associate' },
  { text: 'Bachelor Degree', value: 'bachelor' },
  { text: 'Master Degree', value: 'master' },
  { text: 'Doctorate', value: 'doctorate' },
];

const PREVIOUS_WORK_OPTIONS = [
  { text: 'No', value: 'No' },
  { text: 'Yes - Contractor', value: 'Yes - Contractor' },
  { text: 'Yes - Employee', value: 'Yes - Employee' },
  { text: 'Yes - Retiree', value: 'Yes - Retiree' },
];

const RECRUITING_SOURCE_OPTIONS = [
  {
    text: 'Career Center (unemployment office)',
    value: 'Career Center (unemployment office)',
  },
  { text: 'Career Fair', value: 'Career Fair' },
  { text: 'Employee referral', value: 'Employee referral' },
  {
    text: 'Job Board (LinkedIn, Indeed, etc.)',
    value: 'Job Board (LinkedIn, Indeed, etc.)',
  },
  {
    text: 'Local College/ University Job Board or Career Fair',
    value: 'Local College/ University Job Board or Career Fair',
  },
  { text: 'MAXIMUS referral', value: 'MAXIMUS referral' },
  { text: 'Non Profit Agency (501c3)', value: 'Non Profit Agency (501c3)' },
  { text: 'On-line Advertising', value: 'On-line Advertising' },
  {
    text: 'United Medical Academy (UMA) Initiative',
    value: 'United Medical Academy (UMA) Initiative',
  },
  { text: 'Veteran Sourcing Agency', value: 'Veteran Sourcing Agency' },
];

const VETERAN_OPTIONS = [
  {
    text: 'Candidate declined to disclose',
    value: 'Candidate declined to disclose',
  },
  { text: 'No', value: 'No' },
  { text: 'Yes', value: 'Yes' },
];

const DetailsSection: React.FC<DetailsSectionProps> = ({
  showOnlyRequired,
  job,
}) => {
  return (
    <StepSection index={2} title="Details">
      <Grid spacing={4} direction="column" container>
        <SocialSecurityId
          job={job as Job}
          showOnlyRequired={showOnlyRequired}
        />

        {/* Hidden security ID fields that will be auto-calculated */}
        <Grid item sx={{ display: 'none' }}>
          <ConnectedTextField
            name={modelPath<ApplicationFormState>((i) => i.details.securityId)}
            fullWidth
            variant="filled"
            label="Security ID (Auto-generated)"
            disabled
          />
        </Grid>

        <Grid item>
          <ConnectedDatePicker
            name={modelPath<ApplicationFormState>(
              (i) => i.details.availableDate,
            )}
            inputFormat="MM/dd/yyyy"
            TextFieldProps={{
              variant: 'filled',
              fullWidth: true,
              label: (
                <Typography>
                  Available Date{' '}
                  <Typography component="span" color="error">
                    *
                  </Typography>
                </Typography>
              ),
            }}
          />
        </Grid>

        <Grid item>
          <Typography variant="body2" gutterBottom>
            Submitted to other Job Postings{' '}
            <Typography component="span" color="error">
              *
            </Typography>
          </Typography>
          <ConnectedRadioGroup
            name={modelPath<ApplicationFormState>(
              (i) => i.details.submittedToOtherJobs,
            )}
            options={YES_NO_OPTIONS}
          />
        </Grid>

        <WorkerPayType job={job} showOnlyRequired={showOnlyRequired} />

        <Grid item>
          <Typography variant="body2" gutterBottom>
            Display Candidate's Workforce Record to Buyer? When Submitting This
            Job Seeker{' '}
            <Typography component="span" color="error">
              *
            </Typography>
          </Typography>
          <ConnectedRadioGroup
            name={modelPath<ApplicationFormState>(
              (i) => i.details.displayWorkforceRecord,
            )}
            options={YES_NO_OPTIONS}
          />
        </Grid>

        {!showOnlyRequired && (
          <>
            <Grid item>
              <ConnectedSelect
                name={modelPath<ApplicationFormState>(
                  (i) => i.details.education,
                )}
                options={EDUCATION_OPTIONS}
                fullWidth
                variant="filled"
                label="College Education Completed"
              />
            </Grid>

            <Grid item>
              <ConnectedTextField
                name={modelPath<ApplicationFormState>((i) => i.details.county)}
                fullWidth
                variant="filled"
                label="County"
              />
            </Grid>

            <Grid item>
              <ConnectedSelect
                name={modelPath<ApplicationFormState>(
                  (i) => i.details.previouslyWorked,
                )}
                options={PREVIOUS_WORK_OPTIONS}
                fullWidth
                variant="filled"
                label="Has Candidate Previously Worked at This Client?"
              />
            </Grid>
          </>
        )}

        <LocationCodes job={job} showOnlyRequired={showOnlyRequired} />

        {!showOnlyRequired && (
          <>
            <Grid item>
              <ConnectedSelect
                name={modelPath<ApplicationFormState>(
                  (i) => i.details.recruitingSource,
                )}
                options={RECRUITING_SOURCE_OPTIONS}
                fullWidth
                variant="filled"
                label="Recruiting Source"
              />
            </Grid>

            <Grid item>
              <ConnectedSelect
                name={modelPath<ApplicationFormState>((i) => i.details.veteran)}
                options={VETERAN_OPTIONS}
                fullWidth
                variant="filled"
                label="Veteran (If Disclosed by Candidate)"
              />
            </Grid>
          </>
        )}
      </Grid>
    </StepSection>
  );
};

export default DetailsSection;
